import { apiClient } from './ApiClient';
import { SubAreaDto, CreateSubAreaDto, UpdateSubAreaDto } from '@/domain/entities/SubArea';

/**
 * SubAreaApi - API client for SubArea management operations
 *
 * Implements CRUD operations for SubAreas (geographic areas within Regions).
 * All endpoints are Admin-only and rate-limited (100 requests/min).
 *
 * Backend endpoints: /api/regions/{regionId}/subareas (create/list), /api/subareas/* (other operations)
 */
export class SubAreaApi {
  /**
   * Get all SubAreas for a specific Region
   *
   * @param regionId - The Region ID (human-readable string like "seattle")
   * @returns Array of SubAreaDto objects
   * @throws ApiError if request fails
   *
   * Endpoint: GET /api/regions/{regionId}/subareas
   * Auth: Admin role required
   * Rate limit: 100 requests/min
   */
  async getSubAreasByRegion(regionId: string): Promise<SubAreaDto[]> {
    return apiClient.get<SubAreaDto[]>(`/api/regions/${regionId}/subareas`);
  }

  /**
   * Get a single SubArea by ID
   *
   * @param subAreaId - The SubArea ID (Guid as string)
   * @returns SubAreaDto object
   * @throws ApiError if SubArea not found (404) or request fails
   *
   * Endpoint: GET /api/subareas/{subAreaId}
   * Auth: Admin role required
   * Rate limit: 100 requests/min
   */
  async getSubAreaById(subAreaId: string): Promise<SubAreaDto> {
    return apiClient.get<SubAreaDto>(`/api/subareas/${subAreaId}`);
  }

  /**
   * Create a new SubArea
   *
   * @param dto - CreateSubAreaDto with regionId, name, coordinates, radiusKm
   * @returns Created SubAreaDto with generated subAreaId
   * @throws ApiError if validation fails (400) or request fails
   *
   * Endpoint: POST /api/regions/{regionId}/subareas
   * Auth: Admin role required
   * Rate limit: 100 requests/min
   *
   * Validation rules:
   * - regionId: Must exist
   * - name: Required, max 200 characters
   * - coordinates: latitude (-90 to 90), longitude (-180 to 180)
   * - radiusKm: > 0
   * - isActive: Optional, defaults to true
   */
  async createSubArea(dto: CreateSubAreaDto): Promise<SubAreaDto> {
    return apiClient.post<SubAreaDto>(`/api/regions/${dto.regionId}/subareas`, dto);
  }

  /**
   * Update an existing SubArea (partial updates supported)
   *
   * @param subAreaId - The SubArea ID to update
   * @param dto - UpdateSubAreaDto with optional fields to update
   * @returns void (204 No Content on success)
   * @throws ApiError if SubArea not found (404), validation fails (400), or request fails
   *
   * Endpoint: PUT /api/subareas/{subAreaId}
   * Auth: Admin role required
   * Rate limit: 100 requests/min
   *
   * All fields are optional (partial update):
   * - name: max 200 characters
   * - coordinates: latitude (-90 to 90), longitude (-180 to 180)
   * - radiusKm: > 0
   * - isActive: boolean
   */
  async updateSubArea(subAreaId: string, dto: UpdateSubAreaDto): Promise<void> {
    return apiClient.put<void>(`/api/subareas/${subAreaId}`, dto);
  }

  /**
   * Toggle SubArea active status (isActive: true ↔ false)
   *
   * @param subAreaId - The SubArea ID to toggle
   * @returns void (204 No Content on success)
   * @throws ApiError if SubArea not found (404) or request fails
   *
   * Endpoint: PUT /api/subareas/{subAreaId}/toggle-active
   * Auth: Admin role required
   * Rate limit: 100 requests/min
   */
  async toggleSubAreaActive(subAreaId: string): Promise<void> {
    return apiClient.put<void>(`/api/subareas/${subAreaId}/toggle-active`);
  }

  /**
   * Delete a SubArea
   *
   * IMPORTANT: Cannot delete if it's the last SubArea in the Region.
   * Backend enforces "minimum 1 SubArea per Region" rule.
   *
   * @param subAreaId - The SubArea ID to delete
   * @returns void (204 No Content on success)
   * @throws ApiError if:
   *   - SubArea not found (404)
   *   - Last SubArea in Region (400 with message "Cannot delete the last SubArea")
   *   - Request fails
   *
   * Endpoint: DELETE /api/subareas/{subAreaId}
   * Auth: Admin role required
   * Rate limit: 100 requests/min
   */
  async deleteSubArea(subAreaId: string): Promise<void> {
    return apiClient.delete<void>(`/api/subareas/${subAreaId}`);
  }
}

/**
 * Default SubAreaApi instance
 */
export const subAreaApi = new SubAreaApi();
